import { type ReactNode, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Button, Form, toastSuccess, useUIForm } from "@alpacahq/ui/old";

import { useMutationRequest } from "~/v2/api/hooks";
import {
  type Account,
  editPaperAccount,
  type PaperAccount,
  patchAccount,
} from "~/v2/api/rest/account";
import { Card } from "~/v2/components";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { APIKeys } from "../../home/cards";
import DeleteAccountModal from "../../home/sections/chart/DeleteAccountModal";

const schema = z.object({
  name: z.string(),
});

type PatchAccountNameForm = z.infer<typeof schema>;

type NickNameFormProps = {
  accountId: string;
  type?: string;
};

const NicknameForm = ({ accountId, type = "paper" }: NickNameFormProps) => {
  const {
    refetchPaperAccounts,
    paperAccountsNoDetail,
    refetchLiveAccounts,
    liveAccounts,
  } = useAccountContext();

  const currentPaperAccount = paperAccountsNoDetail?.find(
    (paperAccount) => paperAccount.paper_account_id === accountId,
  );
  const currentLiveAccount = liveAccounts.find(
    (liveAccount) => liveAccount.id === accountId,
  ) as Account;

  const editPaperAccountMutation = useMutationRequest<
    PaperAccount,
    PatchAccountNameForm
  >((formValues) => editPaperAccount(accountId, formValues), {
    onSuccess: () => {
      toastSuccess(`You have successfully edited a paper account.`);
      refetchPaperAccounts();
    },
  });

  const editLiveAccountMutation = useMutationRequest<
    void,
    PatchAccountNameForm
  >(
    (formValues) =>
      patchAccount(accountId, {
        account_name: formValues.name,
      }),
    {
      onSuccess: () => {
        toastSuccess(
          `You have successfully edited your live account nickname.`,
        );

        refetchLiveAccounts();
      },
    },
  );

  const form = useUIForm<PatchAccountNameForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      name:
        type === "paper"
          ? currentPaperAccount?.name
          : (currentLiveAccount?.account_name ?? ""),
    },
  });

  const handleSubmit = (formValues: PatchAccountNameForm) => {
    if (type === "paper") {
      editPaperAccountMutation.mutate(formValues);
    } else {
      editLiveAccountMutation.mutate(formValues);
    }
  };

  return (
    <Form formMethods={form} onSubmit={handleSubmit}>
      <div className="flex flex-row items-end gap-4">
        <Form.TextInput label="Nickname" name="name" />

        <Button type="submit" variant="alternate">
          Save
        </Button>
      </div>
    </Form>
  );
};

const Divider = () => <div className="border border-gray-200"></div>;

type AccountBlockProps = {
  label: string;
  accountId: string;
  accountNumber?: string | null;
  product: "live" | "paper";
  showDivider: boolean;
  headerAction?: ReactNode;
};

const AccountBlock = ({
  label,
  accountId,
  accountNumber,
  product,
  showDivider,
  headerAction,
}: AccountBlockProps) => (
  <div className="space-y-5">
    <div className="flex items-center justify-between">
      <div className="flex gap-2">
        <div>{label}</div>
        <div className="text-gray-400">{accountNumber}</div>
      </div>

      {headerAction}
    </div>

    <div className="grid grid-cols-1 gap-x-4 gap-y-6 min-[500px]:grid-cols-2">
      <NicknameForm accountId={accountId} type={product} />
      <Card title="API Keys">
        <APIKeys key={accountId} product={product} accountID={accountId} />
      </Card>
    </div>

    {showDivider && <Divider />}
  </div>
);

const ManageAccounts = () => {
  const { liveAccounts: allLiveAccounts, paperAccounts } = useAccountContext();

  const liveAccounts = allLiveAccounts.filter((a) => a.status === "ACTIVE");

  const [selectedAccount, setSelectedAccount] = useState<Account>();

  return (
    <div className="max-w-[675px] space-y-8">
      {liveAccounts?.length > 0 && (
        <div className="space-y-5">
          <h2 className="text-xl">Individual Accounts</h2>

          {liveAccounts.map((liveAccount, index) => (
            <AccountBlock
              key={liveAccount.id}
              label="Account"
              accountId={liveAccount.id}
              accountNumber={liveAccount.account_number}
              product="live"
              showDivider={index !== liveAccounts.length - 1}
            />
          ))}
        </div>
      )}

      <div className="space-y-5">
        <h2 className="text-xl">Paper Accounts</h2>

        {paperAccounts.map((paperAccount, index) => (
          <AccountBlock
            key={paperAccount.id}
            label="Paper Account"
            accountId={paperAccount.id}
            accountNumber={paperAccount.account_number}
            product="paper"
            showDivider={index !== paperAccounts.length - 1}
            headerAction={
              <Button
                variant="alternate"
                onClick={() => setSelectedAccount(paperAccount)}
                disabled={paperAccounts.length <= 1}
              >
                Delete Account
              </Button>
            }
          />
        ))}
      </div>

      {selectedAccount && (
        <DeleteAccountModal
          isOpen
          currentAccountId={selectedAccount.id}
          onClose={() => setSelectedAccount(undefined)}
        />
      )}
    </div>
  );
};

export default ManageAccounts;
